import React, { useState, useEffect, useMemo } from 'react';
import { Scarab, scarabs, getScarabsByBaseType } from '../../data/scarabs';
import { ATLAS_MODS, MAPS, INFLUENCE_OPTIONS } from '../../constants';

// Types
interface ScarabSelection {
  id: string;
  scarabId: string;
}



// Main Component
const AtlasDevice: React.FC = () => {
  // State
  const [scarabsByType, setScarabsByType] = useState<Record<string, Scarab[]>>({});
  const [selectedMod, setSelectedMod] = useState<string>('');
  const [selectedInfluence, setSelectedInfluence] = useState<string>('');
  const [selectedMaps, setSelectedMaps] = useState<string[]>([]);
  const [selectedScarabs, setSelectedScarabs] = useState<ScarabSelection[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [activeTab, setActiveTab] = useState<'selected' | 'available'>('selected');
  const [isInfluenceDropdownOpen, setIsInfluenceDropdownOpen] = useState(false);

  // Load scarabs data on mount
  useEffect(() => {
    // Group scarabs by base type
    const groupedScarabs = getScarabsByBaseType();
    setScarabsByType(groupedScarabs);
    setLoading(false);
  }, []);

  // Load saved state from localStorage
  useEffect(() => {
    if (!loading) {
      const savedState = localStorage.getItem('atlasDeviceState');
      if (savedState) {
        try {
          const { mod, selections, influence, maps } = JSON.parse(savedState);
          setSelectedMod(mod || '');
          setSelectedScarabs(selections || []);
          setSelectedInfluence(influence || '');
          setSelectedMaps(maps || []);
        } catch (err) {
          console.error('Failed to parse saved state:', err);
        }
      }
    }
  }, [loading]);

  // Save state to localStorage when it changes
  useEffect(() => {
    if (!loading) {
      localStorage.setItem('atlasDeviceState', JSON.stringify({
        mod: selectedMod,
        selections: selectedScarabs,
        influence: selectedInfluence,
        maps: selectedMaps
      }));
    }
  }, [selectedMod, selectedScarabs, selectedInfluence, selectedMaps, loading]);

  // Filtered scarabs based on search term
  const filteredScarabTypes = useMemo(() => {
    if (!searchTerm.trim()) {
      return Object.keys(scarabsByType).sort();
    }

    const searchLower = searchTerm.toLowerCase();

    // First, filter scarabs that match the search
    const matchingScarabs = Object.values(scarabs).filter(scarab =>
      scarab.name.toLowerCase().includes(searchLower) ||
      scarab.description.toLowerCase().includes(searchLower)
    );

    // Then get the unique base types of those scarabs
    // Using Array.from() instead of spread operator to avoid downlevelIteration issues
    const matchingTypes = Array.from(
      new Set(matchingScarabs.map(s => s.baseType))
    ).sort();

    return matchingTypes;
  }, [scarabsByType, searchTerm]);

  // Filter scarabs within each type
  const getFilteredScarabsOfType = (baseType: string) => {
    if (!searchTerm.trim()) {
      return scarabsByType[baseType];
    }

    const searchLower = searchTerm.toLowerCase();
    return scarabsByType[baseType].filter(scarab =>
      scarab.name.toLowerCase().includes(searchLower) ||
      scarab.description.toLowerCase().includes(searchLower)
    );
  };

  // Event handlers
  const handleModChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedMod(e.target.value);
  };

  const handleMapChange = (map: string) => {
    setSelectedMaps(prev => {
      // If already selected, remove it
      if (prev.includes(map)) {
        return prev.filter(m => m !== map);
      }
      // If we're at max selections (3), don't add
      else if (prev.length >= 3) {
        return prev;
      }
      // Otherwise add it
      else {
        return [...prev, map];
      }
    });
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const addScarab = (scarab: Scarab) => {
    // Check total limit of 5 scarabs
    if (selectedScarabs.length >= 5) {
      alert(`You can only use 5 scarabs in total.`);
      return;
    }

    // Check individual scarab limit
    const scarabsOfThisType = selectedScarabs.filter(selection =>
      selection.scarabId === scarab.id
    );

    if (scarabsOfThisType.length >= scarab.limit) {
      alert(`You can only use ${scarab.limit} of ${scarab.name}`);
      return;
    }

    // Add the selection
    const newSelectedScarabs = [
      ...selectedScarabs,
      { id: `sel-${Date.now()}`, scarabId: scarab.id }
    ];

    setSelectedScarabs(newSelectedScarabs);

    // Switch to selected tab only if we've just added the 5th scarab
    if (newSelectedScarabs.length === 5) {
      setActiveTab('selected');
    }
  };

  const removeScarab = (selectionId: string) => {
    setSelectedScarabs(prev => prev.filter(s => s.id !== selectionId));
  };

  // Render loading state
  if (loading) {
    return <div className="p-4 text-center">Loading scarabs data...</div>;
  }

  // Get the selected scarabs with their full data
  const selectedScarabsWithData = selectedScarabs
    .map(selection => {
      const scarab = scarabs[selection.scarabId];
      return {
        selection,
        scarab
      };
    })
    .filter(item => item.scarab); // Filter out any selections with missing scarabs

  return (
    <div className="bg-gray-800 p-4 rounded-lg border border-gray-700">
      <h2 className="text-lg font-semibold mb-4">Atlas Device</h2>

      {/* Map Modifier Selection */}
      <div className="mb-6">
        <label className="block text-sm font-medium mb-2">Map Modifier:</label>
        <div className="relative">
          <select
            value={selectedMod}
            onChange={handleModChange}
            className="w-full p-2 bg-gray-700 border border-gray-600 rounded text-white appearance-none pr-8"
          >
            <option value="">Select a modifier</option>
            {ATLAS_MODS.map(mod => (
              <option key={mod.id} value={mod.id}>{mod.name}</option>
            ))}
          </select>
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-400">
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
        {selectedMod && (
          <div className="mt-2 text-sm text-gray-400">
            {ATLAS_MODS.find(mod => mod.id === selectedMod)?.description}
          </div>
        )}
      </div>

      {/* Influence Selection */}

      <div className="mb-6">
        <label className="block text-sm font-medium mb-2">Influence:</label>
        <div className="relative">
          <div
            onClick={() => setIsInfluenceDropdownOpen(!isInfluenceDropdownOpen)}
            className="w-full p-2 bg-gray-700 border border-gray-600 rounded text-white appearance-none cursor-pointer flex justify-between items-center"
          >
            {selectedInfluence ? (
              <span className={INFLUENCE_OPTIONS.find(i => i.id === selectedInfluence)?.color}>
                {INFLUENCE_OPTIONS.find(i => i.id === selectedInfluence)?.name}
              </span>
            ) : (
              <span>Select an influence</span>
            )}
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>

          {isInfluenceDropdownOpen && (
            <div className="absolute z-10 mt-1 w-full bg-gray-700 border border-gray-600 rounded shadow-lg">
              <div
                className="p-2 hover:bg-gray-600 cursor-pointer"
                onClick={() => {
                  setSelectedInfluence('');
                  setIsInfluenceDropdownOpen(false);
                }}
              >
                Select an influence
              </div>
              {INFLUENCE_OPTIONS.map(influence => (
                <div
                  key={influence.id}
                  className={`p-2 hover:bg-gray-600 cursor-pointer ${influence.color}`}
                  onClick={() => {
                    setSelectedInfluence(influence.id);
                    setIsInfluenceDropdownOpen(false);
                  }}
                >
                  {influence.name}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Maps Selection */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <label className="text-sm font-semibold text-white">
            Maps ({selectedMaps.length}/3)
          </label>
        </div>

        <div className="bg-gray-700 border border-gray-600 rounded p-3 max-h-40 overflow-y-auto custom-scrollbar">
          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
            {MAPS.map(map => (
              <div key={map} className="flex items-center">
                <button
                  onClick={() => handleMapChange(map)}
                  className={`px-3 py-2 text-sm rounded text-left w-full truncate transition-colors ${selectedMaps.includes(map)
                      ? 'bg-blue-600 hover:bg-blue-500'
                      : 'bg-gray-600 hover:bg-gray-500'
                    }`}
                  disabled={selectedMaps.length >= 3 && !selectedMaps.includes(map)}
                >
                  {map}
                </button>
              </div>
            ))}
          </div>
        </div>

        {selectedMaps.length > 0 && (
          <div className="mt-2 text-sm text-white border-t border-gray-700 pt-2">
            <span className="font-medium">Selected:</span> {selectedMaps.join(', ')}
          </div>
        )}
      </div>

      {/* Tabs for Selected/Available Scarabs */}
      <div className="flex border-b border-gray-700 mb-4">
        <button
          className={`py-2 px-4 font-medium ${activeTab === 'selected'
            ? 'text-blue-400 border-b-2 border-blue-400'
            : 'text-gray-400 hover:text-gray-300'
            }`}
          onClick={() => setActiveTab('selected')}
        >
          Selected Scarabs {selectedScarabsWithData.length > 0 && `(${selectedScarabsWithData.length}/5)`}
        </button>
        <button
          className={`py-2 px-4 font-medium ${activeTab === 'available'
            ? 'text-blue-400 border-b-2 border-blue-400'
            : 'text-gray-400 hover:text-gray-300'
            }`}
          onClick={() => setActiveTab('available')}
        >
          Available Scarabs
        </button>
      </div>

      {/* Search Input (visible in both tabs) */}
      <div className="mb-4">
        <div className="relative">
          <input
            type="text"
            placeholder="Search scarabs by name or effect..."
            value={searchTerm}
            onChange={handleSearchChange}
            className="w-full p-2 pl-10 bg-gray-700 border border-gray-600 rounded text-white"
          />
          <svg
            className="absolute left-3 top-2.5 h-5 w-5 text-gray-400"
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
              clipRule="evenodd"
            />
          </svg>
          {searchTerm && (
            <button
              className="absolute right-3 top-2.5 text-gray-400 hover:text-gray-300"
              onClick={() => setSearchTerm('')}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Selected Scarabs Tab */}
      {activeTab === 'selected' && (
        <div className="mb-6">
          <h3 className="text-lg font-semibold mb-2">Selected Scarabs</h3>

          {selectedScarabsWithData.length > 0 ? (
            <div className="space-y-2">
              {selectedScarabsWithData
                .filter(({ scarab }) =>
                  !searchTerm.trim() ||
                  scarab.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                  scarab.description.toLowerCase().includes(searchTerm.toLowerCase())
                )
                .map(({ selection, scarab }) => (
                  <div
                    key={selection.id}
                    className="flex items-center bg-gray-700 p-2 rounded"
                  >
                    <div className="flex-1">
                      <div className="font-medium">{scarab.name}</div>
                      <div className="text-xs text-gray-400">
                        {scarab.description}
                      </div>
                    </div>
                    <button
                      className="text-red-400 hover:text-red-300 ml-auto transition-colors pr-2"
                      onClick={() => removeScarab(selection.id)}>
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                ))
              }
            </div>
          ) : (
            <div className="text-gray-400 italic p-2">No scarabs selected</div>
          )}
        </div>
      )}

      {/* Available Scarabs Tab */}
      {activeTab === 'available' && (
        <div>
          <h3 className="text-lg font-semibold mb-3">Available Scarabs</h3>

          {filteredScarabTypes.length === 0 ? (
            <div className="text-gray-400 italic p-2">No scarabs match your search</div>
          ) : (
            <div className="space-y-6">
              {filteredScarabTypes.map(baseType => {
                const filteredScarabs = getFilteredScarabsOfType(baseType);

                // Skip rendering if no scarabs match the filter
                if (filteredScarabs.length === 0) return null;

                return (
                  <div key={baseType} className="border border-gray-700 rounded p-3">
                    <div className="flex justify-between items-center mb-2 pb-2 border-b border-gray-700">
                      <h4 className="font-medium">{baseType}</h4>
                      <span className="text-sm bg-gray-700 px-2 py-1 rounded">
                        Total: {selectedScarabs.length}/5
                      </span>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {filteredScarabs.map(scarab => {
                        // Count selections of this specific scarab
                        const scarabSelections = selectedScarabs.filter(
                          selection => selection.scarabId === scarab.id
                        );

                        // Check limits
                        const isAtScarabLimit = scarabSelections.length >= scarab.limit;
                        const isAtTotalLimit = selectedScarabs.length >= 5;
                        const isDisabled = isAtScarabLimit || isAtTotalLimit;

                        return (
                          <div key={scarab.id} className="border border-gray-600 p-2 rounded bg-gray-750">
                            <div className="flex justify-between mb-1">
                              <div className="font-medium">
                                {scarab.variant ? `of ${scarab.variant}` : baseType}
                              </div>
                              <span className="text-xs bg-gray-600 px-2 py-0.5 rounded">
                                {scarabSelections.length}/{scarab.limit}
                              </span>
                            </div>

                            <div className="text-xs text-gray-400 mb-2">
                              {scarab.description}
                            </div>

                            <button
                              onClick={() => addScarab(scarab)}
                              disabled={isDisabled}
                              className={`w-full text-sm py-1 rounded ${isDisabled
                                ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                                : 'bg-blue-600 hover:bg-blue-500'
                                }`}
                            >
                              {isAtScarabLimit
                                ? `Limit Reached (${scarab.limit})`
                                : isAtTotalLimit
                                  ? `Total Limit Reached (5)`
                                  : 'Add Scarab'
                              }
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* End of component with some bottom spacing */}
      <div className="h-4"></div>
    </div>
  );
};

export default AtlasDevice;